import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import { useCaseStore } from '../store/caseStore';
import { useAuthStore } from '../store/authStore';
import { useTaskTemplateStore } from '../store/taskTemplateStore';
import { Task } from '../types';
import { calculateDueDate } from '../utils/dates';
import { CaseDetailsForm } from '../components/case-intake/CaseDetailsForm';
import { TaskAssignmentForm } from '../components/case-intake/TaskAssignmentForm';
import { IntakeFormStepper } from '../components/case-intake/IntakeFormStepper';
import { IntakeFormActions } from '../components/case-intake/IntakeFormActions';

const steps = ['Case Details', 'Task Assignments'];

export const CaseIntake: React.FC = () => {
  const navigate = useNavigate();
  const addCase = useCaseStore(state => state.addCase);
  const { user } = useAuthStore();
  const { getTemplatesForStage } = useTaskTemplateStore();
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    caseNumber: '',
    clientId: '',
    assignedLawyerId: user?.id || '',
    status: 'active' as const,
    practiceArea: '',
    description: '',
  });

  // Stage selection state
  const [selectedStage, setSelectedStage] = useState('new-lead');

  // Get task templates for the selected stage
  const [tasks, setTasks] = useState<Task[]>([]);
  
  useEffect(() => {
    const stageTasks = getTemplatesForStage(selectedStage).map(template => ({
      ...template,
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      assignedTo: user?.id || '',
      dueDate: calculateDueDate(template.dueDate),
    }));
    setTasks(stageTasks);
    
    // Initialize all tasks as selected with default assignments
    setSelectedTasks(stageTasks);
    setTaskAssignments(
      stageTasks.reduce((acc, task) => ({ ...acc, [task.id]: user?.id || '' }), {})
    );
    setTaskDueDates(
      stageTasks.reduce((acc, task) => ({ 
        ...acc, 
        [task.id]: typeof task.dueDate === 'string' ? task.dueDate : calculateDueDate(task.dueDate)
      }), {})
    );
  }, [selectedStage, user?.id, getTemplatesForStage]);

  // Task selection and assignment state
  const [selectedTasks, setSelectedTasks] = useState<Task[]>(tasks);
  const [taskAssignments, setTaskAssignments] = useState<Record<string, string>>({});
  const [taskDueDates, setTaskDueDates] = useState<Record<string, string>>({});

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleSubmit = () => {
    const caseNumber = `${formData.practiceArea.substring(0, 3).toUpperCase()}-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

    const assignedTasks = selectedTasks.map(task => ({
      ...task,
      assignedTo: taskAssignments[task.id],
      dueDate: taskDueDates[task.id],
    }));

    addCase({
      ...formData,
      caseNumber,
      currentStageId: selectedStage,
      tasks: assignedTasks,
      keyDates: [{
        type: 'intake',
        date: new Date().toISOString(),
        description: 'Case intake completed',
      }],
    });

    setShowSuccess(true);
    setTimeout(() => {
      navigate('/cases');
    }, 1500);
  };

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaskToggle = (task: Task) => {
    setSelectedTasks(prev => {
      const exists = prev.some(t => t.id === task.id);
      if (exists) {
        return prev.filter(t => t.id !== task.id);
      } else {
        return [...prev, task];
      }
    });
  };

  const handleTaskAssignment = (taskId: string, userId: string) => {
    setTaskAssignments(prev => ({
      ...prev,
      [taskId]: userId,
    }));
  };

  const handleTaskDueDate = (taskId: string, date: string) => {
    setTaskDueDates(prev => ({
      ...prev,
      [taskId]: date,
    }));
  };

  const handleStageChange = (stageId: string) => {
    setSelectedStage(stageId);
  };

  const isStepValid = () => {
    if (activeStep === 0) {
      return Boolean(
        formData.title &&
        formData.clientId &&
        formData.practiceArea &&
        formData.description
      );
    } else {
      return selectedTasks.every(task => 
        taskAssignments[task.id] && taskDueDates[task.id]
      );
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>New Case Intake</Typography>

      <IntakeFormStepper steps={steps} activeStep={activeStep} />

      <Paper sx={{ p: 4 }}>
        {activeStep === 0 ? (
          <CaseDetailsForm
            formData={formData}
            onChange={handleFormChange}
          />
        ) : (
          <TaskAssignmentForm
            tasks={tasks}
            selectedTasks={selectedTasks}
            taskAssignments={taskAssignments}
            taskDueDates={taskDueDates}
            selectedStage={selectedStage}
            onStageChange={handleStageChange}
            onTaskToggle={handleTaskToggle}
            onTaskAssignment={handleTaskAssignment}
            onTaskDueDate={handleTaskDueDate}
          />
        )}

        <IntakeFormActions
          activeStep={activeStep}
          isValid={isStepValid()}
          onCancel={() => navigate('/cases')}
          onBack={activeStep > 0 ? handleBack : undefined}
          onNext={handleNext}
        />
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" elevation={6} variant="filled">
          Case created successfully! Redirecting...
        </Alert>
      </Snackbar>
    </Box>
  );
};